import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  Box,
  Typography,
  CircularProgress,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Chip,
  Divider
} from '@mui/material';
import { CheckCircle as CheckCircleIcon, Warning as WarningIcon } from '@mui/icons-material';
import { useEnvironment } from '../../../contexts/EnvironmentContext';
import { useWhatsAppConnection } from '../../../contexts/WhatsAppConnectionContext';

/**
 * Connect a WhatsApp Business Account to an environment, or with `replace`, swap the one it has
 * for another. Either way the account lands on `environment`, which is not necessarily the
 * selected one: Settings opens this from every environment's card.
 */
const ConnectWabaDialog = ({ open, onClose, environment, replace = false }) => {
  const { loadWabaConnection } = useEnvironment();
  const { initiateFacebookLogin, initiateClassicFacebookLogin, connectWabaToEnvironment } = useWhatsAppConnection();

  // 'initial' -> ES: 'connecting' | classic: 'discovering' -> 'selecting' -> 'connecting' -> 'success'
  const [step, setStep] = useState('initial');
  const [discoveredWabas, setDiscoveredWabas] = useState([]);
  const [selectedWabaId, setSelectedWabaId] = useState(null);
  const [error, setError] = useState(null);

  const finishConnect = async (onboarding) => {
    await connectWabaToEnvironment(onboarding, { environmentId: environment?.id, replace });
    await loadWabaConnection(environment.id);
    setStep('success');
    setTimeout(() => {
      handleClose();
    }, 1500);
  };

  const handleStartOnboarding = async () => {
    setStep('connecting');
    setError(null);

    try {
      // Meta-hosted Embedded Signup: the user creates/selects a WABA or connects an
      // existing WhatsApp Business app number (coexistence). Returns the onboarded WABA
      // and an authorization code the backend exchanges for a token.
      const onboarding = await initiateFacebookLogin();
      await finishConnect(onboarding);
    } catch (err) {
      console.error('Error connecting WhatsApp Business Account:', err);
      setError(err.message || 'Failed to connect WhatsApp Business Account');
      setStep('initial');
    }
  };

  // The pre-Embedded-Signup path: classic Facebook OAuth + server-side discovery of
  // the WABAs the token can access, then an explicit pick. Kept as a second option
  // because Embedded Signup onboarding requires Meta Advanced Access, while classic
  // OAuth works with standard access for users with a role on the Meta app.
  const handleStartClassic = async () => {
    setStep('discovering');
    setError(null);

    try {
      const result = await initiateClassicFacebookLogin();
      setDiscoveredWabas(result.wabas);
      if (result.wabas.length === 1) {
        setSelectedWabaId(result.wabas[0].wabaId);
      }
      setStep('selecting');
    } catch (err) {
      console.error('Error discovering WhatsApp Business Accounts:', err);
      setError(err.message || 'Failed to discover WhatsApp Business Accounts');
      setStep('initial');
    }
  };

  const handleConnectSelected = async () => {
    const selected = discoveredWabas.find((w) => w.wabaId === selectedWabaId);
    if (!selected) {
      setError('Please select a WhatsApp Business Account');
      return;
    }

    setStep('connecting');
    setError(null);

    try {
      await finishConnect(selected);
    } catch (err) {
      console.error('Error connecting WhatsApp Business Account:', err);
      setError(err.message || 'Failed to connect WhatsApp Business Account');
      setStep('selecting');
    }
  };

  const handleClose = () => {
    if (step !== 'connecting' && step !== 'discovering') {
      setStep('initial');
      setDiscoveredWabas([]);
      setSelectedWabaId(null);
      setError(null);
      onClose();
    }
  };

  const renderContent = () => {
    switch (step) {
      case 'initial':
        return (
          <>
            {replace && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  This environment's flows go back to draft and are set up again on the new number
                  the next time you save or try one. Their copies stay on the old account.
                </Typography>
              </Alert>
            )}

            <Box sx={{ mb: 2 }}>
              <Typography variant="body1" gutterBottom>
                Environment: <strong>{environment?.name}</strong>
              </Typography>

              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                You'll be guided through WhatsApp's signup in a popup, where you can connect a new
                number or an existing WhatsApp Business app number. The selected account is then
                connected to this environment.
              </Typography>

              <Divider sx={{ my: 2 }} />

              <Typography variant="body2" color="text.secondary">
                Already manage a WhatsApp Business Account on this Facebook account? Use the{' '}
                <strong>classic Facebook login</strong> to pick from the accounts you can access.
              </Typography>
            </Box>
          </>
        );

      case 'discovering':
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 3 }}>
            <CircularProgress size={48} sx={{ mb: 2 }} />
            <Typography variant="body1">
              Looking up your WhatsApp Business Accounts...
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Please complete the Facebook login in the popup if prompted
            </Typography>
          </Box>
        );

      case 'selecting':
        return (
          <>
            <Typography variant="body1" gutterBottom>
              Select the WhatsApp Business Account to connect to <strong>{environment?.name}</strong>:
            </Typography>
            <FormControl component="fieldset" sx={{ width: '100%', mt: 1 }}>
              <RadioGroup
                value={selectedWabaId || ''}
                onChange={(e) => setSelectedWabaId(e.target.value)}
              >
                {discoveredWabas.map((waba) => (
                  <FormControlLabel
                    key={waba.wabaId}
                    value={waba.wabaId}
                    control={<Radio />}
                    disabled={!waba.isValid}
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
                        <Box>
                          <Typography variant="body2" fontWeight={600}>{waba.wabaName || waba.wabaId}</Typography>
                          <Typography variant="caption" color="text.secondary">{waba.wabaId}</Typography>
                        </Box>
                        {waba.isValid ? (
                          <Chip size="small" color="success" icon={<CheckCircleIcon />} label="Accessible" />
                        ) : (
                          <Chip size="small" color="warning" icon={<WarningIcon />} label={waba.errorMessage || 'No access'} />
                        )}
                      </Box>
                    }
                  />
                ))}
              </RadioGroup>
            </FormControl>
          </>
        );

      case 'connecting':
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 3 }}>
            <CircularProgress size={48} sx={{ mb: 2 }} />
            <Typography variant="body1">
              Connecting WhatsApp Business Account...
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Please complete the WhatsApp signup in the popup if prompted
            </Typography>
          </Box>
        );

      case 'success':
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 3 }}>
            <CheckCircleIcon color="success" sx={{ fontSize: 64, mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              {replace ? 'Number changed' : 'Connection Successful!'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              WhatsApp Business Account connected to {environment?.name}
            </Typography>
          </Box>
        );

      default:
        return null;
    }
  };

  const renderActions = () => {
    switch (step) {
      case 'initial':
        return (
          <>
            <Button onClick={handleClose}>
              Cancel
            </Button>
            <Button onClick={handleStartClassic} data-testid="connect-classic-login">
              Classic Facebook login
            </Button>
            <Button
              onClick={handleStartOnboarding}
              variant="contained"
            >
              Connect with Facebook
            </Button>
          </>
        );

      case 'selecting':
        return (
          <>
            <Button onClick={handleClose}>
              Cancel
            </Button>
            <Button
              onClick={handleConnectSelected}
              variant="contained"
              disabled={!selectedWabaId}
              data-testid="connect-selected-waba"
            >
              Connect selected account
            </Button>
          </>
        );

      case 'discovering':
      case 'connecting':
        return (
          <Button disabled>
            Please wait...
          </Button>
        );

      case 'success':
        return null;

      default:
        return null;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={step === 'connecting' || step === 'discovering'}
    >
      <DialogTitle>{replace ? 'Change WhatsApp number' : 'Connect WhatsApp Business Account'}</DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {renderContent()}
      </DialogContent>

      <DialogActions>
        {renderActions()}
      </DialogActions>
    </Dialog>
  );
};

export default ConnectWabaDialog;
